import * as z from "zod";

// Raw schema for RPC type inference (no z.ZodType annotation)
export const ToneRangeSchemaRaw = z
  .object({
    min: z.number().int().min(1).max(5),
    max: z.number().int().min(1).max(5),
  })
  .refine((data) => data.min <= data.max, {
    message: "min must be less than or equal to max",
  });

export interface ToneRange {
  min: number;
  max: number;
}

export const ToneRangeSchema: z.ZodType<ToneRange> = ToneRangeSchemaRaw;

export const VOICE_INSTRUCTIONS_MAX_LENGTH = 500;

/**
 * What belongs in the voice instructions and what goes elsewhere. The prompt helper checks merchant
 * text against it, and anything that writes voice instructions follows it, so the two agree.
 */
export const VOICE_INSTRUCTIONS_FIELD_GUIDE = `Voice instructions describe how the brand sounds in every message its AI writes to customers: its personality, word choice, formality, humor, energy, message length, emoji, greetings, and sign-offs. Commerce Agent replies, AI text and email outreach, and the support agent all read them. They are added after the four tone ranges (formality, humor, confidence, and energy), so they shape the voice within those ranges rather than overriding them.

Other things the agent needs go elsewhere:
- Rules about what a message may or may not say, such as topics to avoid or claims not to make, go in guidelines, which check every Commerce Agent message before it's sent.
- Store facts, such as policies, prices, shipping, or product details, go in knowledge, so they stay current.
- Instructions for one automation, campaign, or channel go in that automation's strategy.
- Word-for-word example messages get copied instead of adapted, so the voice is described rather than scripted. A greeting or sign-off the brand uses every time is fine.`;

// Raw schema for RPC type inference (no z.ZodType annotation)
export const AiToneAndVoiceSettingsSchemaRaw = z.object({
  voiceInstructions: z.string().max(VOICE_INSTRUCTIONS_MAX_LENGTH).optional(),
  reasoning: z.string().max(300).optional(),
  formalityCasualness: ToneRangeSchemaRaw,
  seriousnessHumor: ToneRangeSchemaRaw,
  respectfulnessIrreverence: ToneRangeSchemaRaw,
  matterOfFactEnthusiasm: ToneRangeSchemaRaw,
});

export interface AiToneAndVoiceSettings {
  voiceInstructions?: string;
  reasoning?: string;
  formalityCasualness: ToneRange;
  seriousnessHumor: ToneRange;
  respectfulnessIrreverence: ToneRange;
  matterOfFactEnthusiasm: ToneRange;
}

export const AiToneAndVoiceSettingsSchema: z.ZodType<AiToneAndVoiceSettings> =
  AiToneAndVoiceSettingsSchemaRaw;

const aiToneAndVoiceSettingsUpdateSchema_ = z.object({
  voiceInstructions: z.string().max(VOICE_INSTRUCTIONS_MAX_LENGTH).optional(),
  reasoning: z.string().max(300).optional(),
  formalityCasualness: ToneRangeSchemaRaw.optional(),
  seriousnessHumor: ToneRangeSchemaRaw.optional(),
  respectfulnessIrreverence: ToneRangeSchemaRaw.optional(),
  matterOfFactEnthusiasm: ToneRangeSchemaRaw.optional(),
});
export const AiToneAndVoiceSettingsUpdateSchema: z.ZodType<AiToneAndVoiceSettingsUpdate> =
  aiToneAndVoiceSettingsUpdateSchema_;
export interface AiToneAndVoiceSettingsUpdate extends z.infer<
  typeof aiToneAndVoiceSettingsUpdateSchema_
> {}

export const aiToneAndVoiceSettingsDefault: AiToneAndVoiceSettings = {
  formalityCasualness: { min: 2, max: 4 },
  seriousnessHumor: { min: 2, max: 4 },
  respectfulnessIrreverence: { min: 2, max: 4 },
  matterOfFactEnthusiasm: { min: 2, max: 4 },
};
